import {Injectable, inject, resource, computed} from '@angular/core';
import {firstValueFrom, of} from 'rxjs';
import {GmMapConfig, GmSource} from '../models/gm-map-config.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import {GameData, GAME_DATA_GENERAL_DEFAULTS, Question, Theme} from '../models/game-data.model';
import texts from '../language/nl.json';
import {TextDataModel} from "../models/text-data.model";
import {FeatureCollection} from 'geojson';
import {withDefaults} from '../utils/with-defaults';

export type ThemeWithQuestions = Theme & { questions: Question[]; };

@Injectable({
  providedIn: 'root'
})
export class ResourcesService {
  private readonly http = inject(HttpClient);

  private readonly configBaseUrl = 'https://services.geodan.nl/public/document/default/api/data/IENW1544NEXT/config/';
  private readonly documenterBaseUrl = 'https://services.geodan.nl/public/document/default/api/data/IENW1544NEXT/gisDePlek/';
  private readonly accQuestionsFileName = 'acc-game-data';
  private readonly prodQuestionsFileName = 'game-data';
  private readonly accLanguageFileName = 'acc-nl';
  private readonly prodLanguageFileName = 'nl';

  private readonly grabbeltonTheme = computed(() => {
    const texts = this.languageFile.value();
    const gameData = this.gameData.value();
    return {
      id: null,
      name: texts.allThemes,
      isActive: true,
      imageURL: {url: 'assets/images/wandelen-natuur.jpg', alt: ''},
      questions: gameData?.questions ?? []
    };
  })

  public readonly themes = computed<ThemeWithQuestions[]>(
    () => {
      const gameData = this.gameData.value();
      if (!gameData){
        return [];
      }
      const allQuestions = gameData?.questions ?? [];
      const withQuestion: (theme: Theme) => ThemeWithQuestions = (theme: Theme) => ({ ...theme, questions: allQuestions.filter(q => q.themeId === theme.id)});
      const allThemes: ThemeWithQuestions[] = (gameData?.themes ?? []).map(withQuestion);

      const isLarge = (theme: ThemeWithQuestions)=> theme.questions.length >= gameData.general.randomQuestionsPerGame;
      const activeThemes = allThemes.filter(t => t.isActive).filter(isLarge);
      return [this.grabbeltonTheme(), ...activeThemes];
    }
  );

  public themeById(themeId: number | null): (Theme & { questions: Question[] }) | undefined {
    return this.themes().find(theme => theme.id === themeId);
  }

  isAcc() {
    return window.location.href.includes('https://atlas-acc.apps.geodan.nl')
      || window.location.href.includes('https://atlas-test.apps.geodan.nl')
      || window.location.href.includes('localhost');
  }

  public mapConfig = resource<GmMapConfig, {uuid: string} | undefined>({
    params: () => {
      // Stay idle until game data provides a real config id. Requesting with an
      // empty uuid produces a spurious server error that surfaces as a
      // `mapConfigError` even though the map ultimately loads fine.
      const uuid = this.gameData.value()?.general.viewerConfigName;
      return uuid ? {uuid} : undefined;
    },
    loader: ({params: { uuid }}) => firstValueFrom(
      this.http.get<GmMapConfig>(`${this.configBaseUrl}${uuid}`))
  });

  public gameData = resource<GameData, {fileName: string}>({
    params: () => ({fileName: this.isAcc() ? this.accQuestionsFileName : this.prodQuestionsFileName}),
    loader: async ({params: { fileName }}) => {
      const loaded = await firstValueFrom(
        this.http.get<GameData>(`${this.documenterBaseUrl}${fileName}`));
      return {...loaded, general: withDefaults(GAME_DATA_GENERAL_DEFAULTS, loaded.general)};
    }
  });

  public languageFile = resource<TextDataModel, {fileName: string}>({
    params: () => ({fileName: this.isAcc() ? this.accLanguageFileName : this.prodLanguageFileName}),
    loader: async ({params: {fileName}}) => {
      const loaded = await firstValueFrom(
        this.http.get<Partial<TextDataModel>>(`${this.documenterBaseUrl}${fileName}`)
      );
      return withDefaults(texts as TextDataModel, loaded);
    },
    defaultValue: texts as TextDataModel
  });

  getFeatureCollection(source: GmSource) {
    if (source.type !== 'OGC_WFS') {
      return of({type: 'FeatureCollection', features: []} as FeatureCollection)
    }

    const urlObject = new URL(source.url);
    const url = `${urlObject.origin}${urlObject.pathname}`;
    const params = new HttpParams()
      .set('service', 'wfs')
      .set('request', 'getFeature')
      .set('typeName', source.featureName)
      .set('version', source.version ?? '2.0.0')
      .set('outputFormat', 'application/json');

    return this.http.get<FeatureCollection>(url, {params});
  }
}
